import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import appConfig from '../AppConfig';
import {
  validateSession,
  handleSessionExpiry,
  SESSION_CHECK_INTERVAL_MS,
} from '../utils/sessionGuard';
import { useAuth } from '../context/AuthContext';

export interface UseSessionGuardOptions {
  /** Interval in ms for periodic checks. Defaults to SESSION_CHECK_INTERVAL_MS (5 min). */
  intervalMs?: number;
  /** Paths that should be excluded from the guard (e.g. /login, /health). */
  excludedPaths?: string[];
}

/**
 * React hook that enforces an active authenticated session.
 *
 * - Runs a session check **on every route change** (location.pathname).
 * - Runs a **periodic background check** (default every 5 minutes).
 * - If the session is invalid and cannot be extended, logs the user out
 *   via AuthContext and redirects to `/login`.
 *
 * Place this hook inside a component rendered within both `<MsalProvider>`
 * and `<BrowserRouter>` (e.g. `<App />`).
 */
export function useSessionGuard(options: UseSessionGuardOptions = {}) {
  const {
    intervalMs = SESSION_CHECK_INTERVAL_MS,
    excludedPaths = ['/login', '/health'],
  } = options;

  const { instance } = useMsal();
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const checkInProgress = useRef(false);

  const performCheck = useCallback(async () => {
    // Skip check for excluded paths
    if (excludedPaths.some((p) => location.pathname.startsWith(p))) {
      return;
    }

    // Prevent concurrent checks
    if (checkInProgress.current) return;
    checkInProgress.current = true;

    try {
      const result = await validateSession(instance, appConfig.azureAd.accessScopes);

      if (!result.valid) {
        // Session cannot be extended – force logout
        await handleSessionExpiry(instance);
        await logout();
        navigate('/login', { replace: true });
      }
    } catch {
      // Unrecoverable – treat as session loss
      await handleSessionExpiry(instance);
      await logout();
      navigate('/login', { replace: true });
    } finally {
      checkInProgress.current = false;
    }
  }, [instance, logout, navigate, location.pathname, excludedPaths]);

  // Check on every route change
  useEffect(() => {
    performCheck();
  }, [performCheck]);

  // Periodic background check
  useEffect(() => {
    if (intervalMs <= 0) return;
    const id = setInterval(performCheck, intervalMs);
    return () => clearInterval(id);
  }, [performCheck, intervalMs]);
}
